/**
 * Parties — DR-04, DR-05, IR-02, IR-03, FR-09, FR-10.
 *
 * A party is a **reference** to a system of record, never a copy of it (DR-04).
 * Only a display name is cached, and it is labelled as a cache so nobody treats
 * it as authoritative.
 */
import { z } from 'zod';
import { ClientPartyKeySchema, IsoTimestampUtcSchema, PartnerPartyKeySchema, PartyKeySchema, UpnSchema, VendorPartyKeySchema, } from '../primitives.js';
import { ContractPartyRoleSchema } from '../vocabulary.js';
import { RegionSchema, RegionSourceSchema } from './region.js';
import { ContractPartyVendorReferenceSchema, VendorImportBatchIdSchema } from './vendor-reference.js';
/**
 * CLM-99 — where `party.billing_client_id` came from. One value today
 * (`qfly_sync` — read from Qfly by `party-sync`, the only writer, D-99-5: no
 * admin override in this story). Widening this later is an ordinary migration
 * that adds a value to `ck_party_billing_client_id_source`'s `IN (...)` list
 * (0090's own precedent), not something to guess room for now.
 */
export const BILLING_CLIENT_ID_SOURCES = ['qfly_sync'];
export const BillingClientIdSourceSchema = z.enum(BILLING_CLIENT_ID_SOURCES);
/** Fields common to all three party types. */
const partyBase = {
    /**
     * Reference into the system of record. DR-04 — the CLM index points at it and
     * never duplicates it.
     */
    sor_reference_id: z.string().min(1).max(64),
    /**
     * Display cache only. Refreshed from the system of record; if it disagrees with
     * Qfly or Dynamics, they win.
     */
    cached_display_name: z.string().min(1).max(200),
    cached_at_utc: IsoTimestampUtcSchema,
    /** Corporate family (DR-05). A party's parent is always the same party type. */
    parent_party_key: PartyKeySchema.nullable(),
    is_active: z.boolean(),
    /**
     * §4C / decision 3 — set at *party* level in Qfly and inherited by contracts,
     * with a per-contract override. Legal decides once per client, not once per
     * document. A restricted party's contracts never reach an LLM.
     */
    processing_restricted: z.boolean(),
    /**
     * CLM-87 — the client's region, set once by the first verified counterparty
     * country (`verified_contract`) or by an admin (`admin`); null = Unassigned.
     * Both null or both set (`ck_party_region_pair`, 0094).
     */
    region: RegionSchema.nullable(),
    region_source: RegionSourceSchema.nullable(),
    /**
     * CLM-99 — the Dynamics billing client ID, read from Qfly by party-sync;
     * never invented or transformed (R-99-1). `is_billable` is GENERATED in the
     * database from this column's presence alone (0096) — it is never a second
     * fact this schema could disagree with, so it is typed as plain `boolean`,
     * not derived again on the wire.
     */
    billing_client_id: z.string().min(1).max(64).nullable(),
    billing_client_id_source: BillingClientIdSourceSchema.nullable(),
    is_billable: z.boolean(),
};
/**
 * A party, discriminated on `party_type`.
 *
 * The union is the point. `architecture.md` §7.3 enforces IR-02/IR-03 in the
 * database — *"a Vendor physically cannot be recorded as resolving to Qfly, and a
 * Client cannot resolve to Dynamics. That is the control, not a comment."* This
 * mirrors that control at the API boundary, and it also pins the TD-02 key prefix
 * to the type, so `{ party_type: 'Vendor', party_key: 'SGW' }` does not typecheck.
 */
export const PartySchema = z.discriminatedUnion('party_type', [
    z.object({
        party_type: z.literal('Client'),
        party_key: ClientPartyKeySchema,
        sor_system: z.literal('Qfly'),
        ...partyBase,
    }),
    z.object({
        party_type: z.literal('Partner'),
        party_key: PartnerPartyKeySchema,
        sor_system: z.literal('Qfly'),
        ...partyBase,
    }),
    z.object({
        party_type: z.literal('Vendor'),
        party_key: VendorPartyKeySchema,
        /**
         * IR-03 stands: Dynamics is the system of record for vendor master data.
         * `'CLM'` is the R-76-2 interim (CLM-78, migration 0090): a vendor registered
         * from a typed name until the Dynamics adapter reconciles it in place.
         */
        sor_system: z.enum(['Dynamics', 'CLM']),
        ...partyBase,
    }),
]);
/**
 * The lifecycle of a party alias as the wire reports it — D-B8 (c).
 *
 * Derived, not stored: migration 0004 records `source`, `is_confirmed` and
 * `is_active`, and the four states are the readable names for the combinations
 * that matter to a reviewer. The SQL `CASE` that derives them lives beside the
 * statements in `apps/api/src/data/party-alias-queries.ts`.
 */
export const PARTY_ALIAS_STATES = ['pending', 'active', 'rejected', 'retired'];
export const PartyAliasStateSchema = z.enum(PARTY_ALIAS_STATES);
/**
 * A known written variant of a party name — §4A.4.
 *
 * Aliases are what make FR-29 substitution work on real documents: Qfly gives
 * *Southern Glazer's Wine & Spirits, LLC*, and the contract says *Southern
 * Glazers Wine and Spirits LLC*.
 */
export const PartyAliasSchema = z.object({
    alias_text: z.string().min(1).max(200),
    /** `Detected` aliases come from the §4A.5 leak detector and start unconfirmed. */
    source: z.enum(['Qfly', 'Manual', 'Detected']),
    /**
     * §4A.4 `ck_alias_short_confirmed` is the control: an alias shorter than 5
     * characters cannot be active unless a human confirmed it. Acronyms like `SGWS`
     * are explicit entries only — a three-letter substitution loose in a contract
     * would corrupt unrelated text.
     */
    is_confirmed: z.boolean(),
    /**
     * Soft retirement (migration 0004, addition 1). A retired alias stays on the
     * row so the map history behind §4A.7's evidence claim survives; it is simply
     * no longer compiled into the substitution map.
     */
    is_active: z.boolean(),
    /**
     * The lifecycle state, derived on the wire from `source`, `is_confirmed` and
     * `is_active` — D-B8 (c). There is no status column: `pending` is a `Detected`
     * proposal nobody has approved, `active` is what the next pseudonymisation run
     * substitutes, `rejected` is a proposal a human turned down (the row is kept so
     * the same variant cannot be re-queued), `retired` is every other inactive row.
     */
    state: PartyAliasStateSchema,
    /** Who approved it and when — `null` for `Qfly` (canonical) and unconfirmed rows. */
    confirmed_by_upn: UpnSchema.nullable(),
    confirmed_utc: IsoTimestampUtcSchema.nullable(),
    /**
     * Why the last human decision was taken — migration 0084, D-B8. `null` for
     * `Qfly` rows and for proposals the pipeline inserted; a reject or retire always
     * carries one, because the reason is the NFR-13 evidence.
     */
    decision_note: z.string().min(1).max(1000).nullable(),
    /**
     * CLM-114 · R-114-1 — the vendor-list upload this alias came from (an
     * imported name, or a pending proposal on the row's own party that the
     * import confirmed in place, R-114-2). `null` for every alias a person or the
     * leak detector added. Only ever set on a confirmed alias
     * (`ck_party_alias_import_confirmed`).
     */
    import_batch_id: VendorImportBatchIdSchema.nullable(),
    created_by_upn: UpnSchema.nullable(),
    created_utc: IsoTimestampUtcSchema,
});
/**
 * A party as it appears on one contract — the FR-09 multi-party junction.
 *
 * `is_primary` is not decoration: DR-01 makes the primary party the one whose key
 * forms the contract key, and there is exactly one per contract.
 */
export const ContractPartySchema = z.object({
    party_key: PartyKeySchema,
    party_role: ContractPartyRoleSchema,
    is_primary: z.boolean(),
    cached_display_name: z.string().min(1).max(200),
    /**
     * The token this party's name was substituted for before anything left the
     * tenant — FR-29, e.g. `[CLIENT:SGW]`. Present once pseudonymisation has run;
     * `null` on a `processing_restricted` contract, which never crosses the
     * boundary. Surfaced so an extracted summary that still refers to a token can
     * be rendered with the real name.
     */
    pseudonym_token: z.string().min(1).max(64).nullable(),
    /** CLM-99 — projected from this party's own row (R-99-1 item 4: "follow exactly how region and cloudEQ entity were threaded"). */
    billing_client_id: z.string().min(1).max(64).nullable(),
    is_billable: z.boolean(),
    /**
     * CLM-114 · R-114-5 — the vendor-list cards that identify this party
     * ("Vendor no. *X* · cloudEQ *entity*"): the visible proof that the contract
     * is identified against the list. Empty for a Client or Partner, and for a
     * vendor no list has named. Read inside the existing scoped parties statement.
     */
    vendor_references: z.array(ContractPartyVendorReferenceSchema),
});
//# sourceMappingURL=party.js.map